import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import {
  generateAllInvoices,
  generateInvoice,
  getGeneratedInvoiceDetail,
  getGeneratedInvoices,
  getGeneratedInvoiceVersions,
} from './generated-invoice.api'
import {
  FundingInvoiceDetailDto,
  FundingInvoiceListResponseDto,
  FundingInvoiceVersionsResponseDto,
  GenerateAllInvoicesDto,
  GenerateAllResultDto,
  GenerateInvoiceDto,
  GenerateResultDto,
  GetGeneratedInvoicesRequestDto,
} from './dto/generated-invoice.dto'

export const GENERATED_INVOICE_LIST_QUERY_KEY = 'generated-invoice-list'
export const GENERATED_INVOICE_DETAIL_QUERY_KEY = 'generated-invoice-detail'
export const GENERATED_INVOICE_VERSIONS_QUERY_KEY = 'generated-invoice-versions'

export const useGetGeneratedInvoicesQuery = (
  params: GetGeneratedInvoicesRequestDto,
  options?: Omit<
    UseQueryOptions<
      FundingInvoiceListResponseDto,
      Error,
      FundingInvoiceListResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    FundingInvoiceListResponseDto,
    Error,
    FundingInvoiceListResponseDto
  >({
    queryKey: [GENERATED_INVOICE_LIST_QUERY_KEY, params],
    queryFn: () => getGeneratedInvoices(params),
    ...options,
  })
}

export const useGetGeneratedInvoiceDetailQuery = (
  id: string | null,
  options?: Omit<
    UseQueryOptions<FundingInvoiceDetailDto, Error, FundingInvoiceDetailDto>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<FundingInvoiceDetailDto, Error, FundingInvoiceDetailDto>({
    queryKey: [GENERATED_INVOICE_DETAIL_QUERY_KEY, id],
    queryFn: () => getGeneratedInvoiceDetail(id as string),
    enabled: Boolean(id),
    ...options,
  })
}

export const useGetGeneratedInvoiceVersionsQuery = (
  id: string | null,
  options?: Omit<
    UseQueryOptions<
      FundingInvoiceVersionsResponseDto,
      Error,
      FundingInvoiceVersionsResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    FundingInvoiceVersionsResponseDto,
    Error,
    FundingInvoiceVersionsResponseDto
  >({
    queryKey: [GENERATED_INVOICE_VERSIONS_QUERY_KEY, id],
    queryFn: () => getGeneratedInvoiceVersions(id as string),
    enabled: Boolean(id),
    ...options,
  })
}

export const useGenerateInvoiceMutation = (
  options?: UseMutationOptions<GenerateResultDto, Error, GenerateInvoiceDto>
) => {
  const queryClient = useQueryClient()
  return useMutation<GenerateResultDto, Error, GenerateInvoiceDto>({
    ...options,
    mutationFn: generateInvoice,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [GENERATED_INVOICE_LIST_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [GENERATED_INVOICE_VERSIONS_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [GENERATED_INVOICE_DETAIL_QUERY_KEY],
      })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useGenerateAllInvoicesMutation = (
  options?: UseMutationOptions<
    GenerateAllResultDto,
    Error,
    GenerateAllInvoicesDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<GenerateAllResultDto, Error, GenerateAllInvoicesDto>({
    ...options,
    mutationFn: generateAllInvoices,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [GENERATED_INVOICE_LIST_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [GENERATED_INVOICE_VERSIONS_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [GENERATED_INVOICE_DETAIL_QUERY_KEY],
      })
      options?.onSuccess?.(data, variables, context)
    },
  })
}
